/**
 * Knowledge API Service
 * Handles all knowledge-related API calls for managing items and collections
 */

import { api, LONG_TIMEOUT } from './api-client';

/**
 * Knowledge Items API endpoints
 */
export const knowledgeItemsApi = {
  /**
   * Create new knowledge item
   * @param {Object} itemData - Item data including content and collection links
   * @param {string} itemData.content - Content of the knowledge item
   * @param {string} [itemData.title] - Optional title (will be auto-generated if not provided)
   * @param {string} [itemData.image] - Optional image URL
   * @param {string} [itemData.attached_item] - Optional attached item reference
   * @param {Array<string>} itemData.collection_ids - Array of collection UUIDs to link to
   * @returns {Promise<Object>} Created item with linked collections
   */
  async create(itemData) {
    return await api.post('/api/v1/knowledge/items', itemData);
  },

  /**
   * Get all knowledge items
   * @returns {Promise<Array>} Array of knowledge items
   */
  async getAll() {
    return await api.get('/api/v1/knowledge/items');
  },

  /**
   * Get specific knowledge item by ID
   * @param {string} itemId - Item UUID
   * @returns {Promise<Object>} Knowledge item object
   */
  async getById(itemId) {
    return await api.get(`/api/v1/knowledge/items/${itemId}`);
  },

  /**
   * Update knowledge item
   * @param {string} itemId - Item UUID
   * @param {Object} itemData - Updated item data
   * @returns {Promise<Object>} Updated item
   */
  async update(itemId, itemData) {
    return await api.put(`/api/v1/knowledge/items/${itemId}`, itemData);
  },

  /**
   * Delete knowledge item
   * @param {string} itemId - Item UUID
   * @returns {Promise<void>}
   */
  async delete(itemId) {
    return await api.delete(`/api/v1/knowledge/items/${itemId}`);
  },

  /**
   * Move item between slots (same or different collection)
   * @param {Object} moveData - Move operation details
   * @param {string} moveData.itemId - ID of the item being moved
   * @param {string} moveData.sourceCollectionId - Source collection ID
   * @param {number} moveData.sourceSlotPosition - Source slot position (0-3)
   * @param {string} moveData.targetCollectionId - Target collection ID
   * @param {number} moveData.targetSlotPosition - Target slot position (0-3)
   * @returns {Promise<Object>} Move operation result
   */
  async move(moveData) {
    return await api.post('/api/v1/knowledge/items/move', {
      item_id: moveData.itemId,
      source_collection_id: moveData.sourceCollectionId,
      source_slot_position: moveData.sourceSlotPosition,
      target_collection_id: moveData.targetCollectionId,
      target_slot_position: moveData.targetSlotPosition
    });
  },

  /**
   * Link item to a collection (for shared slots)
   * @param {Object} linkData - Link operation details
   * @param {string} linkData.itemId - ID of the item to link
   * @param {string} linkData.collectionId - Collection ID to link to
   * @param {number} linkData.slotPosition - Slot position (0-3)
   * @returns {Promise<Object>} Link operation result
   */
  async link(linkData) {
    return await api.post('/api/v1/knowledge/items/link', {
      item_id: linkData.itemId,
      collection_id: linkData.collectionId,
      slot_position: linkData.slotPosition
    });
  },

  /**
   * Unlink item from a collection (when removing from shared slots)
   * @param {Object} unlinkData - Unlink operation details
   * @param {string} unlinkData.itemId - ID of the item to unlink
   * @param {string} unlinkData.collectionId - Collection ID to unlink from
   * @returns {Promise<Object>} Unlink operation result
   */
  async unlink(unlinkData) {
    return await api.post('/api/v1/knowledge/items/unlink', {
      item_id: unlinkData.itemId,
      collection_id: unlinkData.collectionId
    });
  },
};

/**
 * Knowledge Collections API endpoints
 */
export const knowledgeCollectionsApi = {
  /**
   * Get all knowledge collections
   * @returns {Promise<Array>} Array of knowledge collections
   */
  async getAll() {
    const response = await api.get('/api/v1/knowledge/collections');
    return response.collections || [];
  },

  /**
   * Search collections by title and item contents
   * @param {string} query - Search query
   * @returns {Promise<Array>} Array of matching collections sorted by relevance
   */
  async search(query) {
    if (!query || !query.trim()) {
      return [];
    }
    const response = await api.get(`/api/v1/knowledge/collections/search?q=${encodeURIComponent(query)}`);
    return response.collections || [];
  },

  /**
   * Get specific collection by ID
   * @param {string} collectionId - Collection UUID
   * @returns {Promise<Object>} Collection object
   */
  async getById(collectionId) {
    return await api.get(`/api/v1/knowledge/collections/${collectionId}`);
  },

  /**
   * Create new collection
   * @param {Object} collectionData - Collection data
   * @returns {Promise<Object>} Created collection
   */
  async create(collectionData) {
    return await api.post('/api/v1/knowledge/collections', collectionData);
  },

  /**
   * Update collection
   * @param {string} collectionId - Collection UUID
   * @param {Object} collectionData - Updated collection data
   * @returns {Promise<Object>} Updated collection
   */
  async update(collectionId, collectionData) {
    return await api.put(`/api/v1/knowledge/collections/${collectionId}`, collectionData);
  },

  /**
   * Delete collection
   * @param {string} collectionId - Collection UUID
   * @returns {Promise<void>}
   */
  async delete(collectionId) {
    return await api.delete(`/api/v1/knowledge/collections/${collectionId}`);
  },
};

/**
 * Knowledge Products API endpoints
 */
export const knowledgeProductsApi = {
  /**
   * Get all products
   * @returns {Promise<Array>} Array of products with id, name, and version
   */
  async getAll() {
    const response = await api.get('/api/v1/knowledge/products');
    return response.products || [];
  },

  /**
   * Create new product
   * @param {Object} productData - Product data
   * @param {string} productData.name - Product name (required)
   * @param {string} [productData.version] - Product version (optional)
   * @returns {Promise<Object>} Created product
   */
  async create(productData) {
    return await api.post('/api/v1/knowledge/products', productData);
  },
};

/**
 * Knowledge Filters API endpoints
 */
export const knowledgeFiltersApi = {
  /**
   * Get all products
   * @returns {Promise<Array>} Array of products with id, name, and version
   */
  async getProducts() {
    const response = await api.get('/api/v1/knowledge/products');
    return response.products || [];
  },

  /**
   * Get all categories
   * @returns {Promise<Array>} Array of category strings
   */
  async getCategories() {
    const response = await api.get('/api/v1/knowledge/categories');
    return response.categories || [];
  },
};

/**
 * Document Import API endpoints
 */
export const documentImportApi = {
  /**
   * Process PDF document (preprocess and structure with AI)
   * @param {File} file - PDF file to process
   * @param {number} [thresholdPercentage=50] - Threshold for repeated content detection
   * @returns {Promise<Object>} Structured document data for preview
   */
  async processDocument(file, thresholdPercentage = 50) {
    const formData = new FormData();
    formData.append('file', file);

    return await api.post(
      `/api/v1/documents/import/process?threshold_percentage=${thresholdPercentage}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: LONG_TIMEOUT, // 5 minutes timeout for document processing (AI structuring can take time)
      }
    );
  },

  /**
   * Execute import of structured document
   * @param {Object} documentData - Structured document data from processing
   * @param {string} productId - Product ID to import into
   * @returns {Promise<Object>} Import report with created collections and items
   */
  async executeImport(documentData, productId) {
    return await api.post(
      `/api/v1/documents/import/execute?product_id=${productId}`,
      documentData,
      {
        timeout: LONG_TIMEOUT, // 5 minutes timeout for import execution (creating items, generating metadata, embeddings)
      }
    );
  },

  /**
   * Preview import without executing
   * @param {Object} documentData - Structured document data
   * @param {string} productId - Product ID to preview for
   * @returns {Promise<Object>} Preview with statistics and sample data
   */
  async previewImport(documentData, productId) {
    return await api.post(
      `/api/v1/documents/import/preview?product_id=${productId}`,
      documentData
    );
  },
};

/**
 * Combined knowledge API export
 */
export const knowledgeApi = {
  items: knowledgeItemsApi,
  collections: knowledgeCollectionsApi,
  products: knowledgeProductsApi,
  filters: knowledgeFiltersApi,
  documentImport: documentImportApi,
};

export default knowledgeApi;
